import React, { useEffect } from "react";

import { Button, Divider, Form } from "@samskip/frontend-components";
import { navigate } from "gatsby";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ShippingTypeBookingTemplates,
  NavigationConstants,
} from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import {
  defaultCargoForm,
  defaultDetailForm,
} from "@assets/formTypes/bookingTemplates";
import { useAppDispatch } from "@assets/hooks";
import { CommonTypes } from "@assets/types";
import { WindowUtilities } from "@assets/utilities";
import {
  CommentsSection,
  ShipmentSection,
  CargoSection,
  BillOfLadingSection,
  HSCodesSection,
  LeavePageModal,
} from "@components";
import { getAllCommodities } from "@state/slices/commoditySlice";
import {
  getContainerTypes,
  getPackageTypes,
} from "@state/slices/registrySlice";

interface BookingTemplatesCargoFormProps {
  formValues: BookingTemplateFormTypes.CargoForm;
  onPrevClick: CommonTypes.OneParamCallbackFunction<BookingTemplateFormTypes.CargoForm>;
  onNextClick: CommonTypes.OneParamCallbackFunction<BookingTemplateFormTypes.CargoForm>;
  isSubmitting: boolean;
  onCancelClick: CommonTypes.CallbackFunction;
}

const BookingTemplatesCargo = ({
  formValues,
  onPrevClick,
  onNextClick,
  isSubmitting,
  onCancelClick,
}: BookingTemplatesCargoFormProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit, register, setValue, getValues } = useForm({
    defaultValues: formValues,
    mode: "onTouched",
  });
  const dispatch = useAppDispatch();

  const handlePrevClick = (data: BookingTemplateFormTypes.CargoForm) => {
    onPrevClick(data);
  };

  const handleNextClick = (data: BookingTemplateFormTypes.CargoForm) => {
    onNextClick(data);
  };

  const handleResetDetails = () => {
    setValue("Details", defaultCargoForm.Details);
  };

  const handleResetDescription = (cargoIndex: number) => {
    setValue(
      `Details.${cargoIndex}.CargoDescription`,
      defaultDetailForm.CargoDescription
    );
  };

  const resetTemperatureFields = (cargoIndex: number) => {
    if (getValues("ShippingType") !== ShippingTypeBookingTemplates.LL) {
      setValue(
        `Details.${cargoIndex}.Refrigerated`,
        defaultDetailForm.Refrigerated
      );
      resetTemperatureControlledFields(cargoIndex);
    }
  };

  const resetTemperatureControlledFields = (cargoIndex: number) => {
    setValue(
      `Details.${cargoIndex}.Temperature`,
      defaultDetailForm.Temperature
    );
    resetVentilation(cargoIndex);
  };

  const resetVentilation = (cargoIndex: number) => {
    setValue(
      `Details.${cargoIndex}.VentilationVolume`,
      defaultDetailForm.VentilationVolume
    );
  };

  const handleSetTemperatureControl = (cargoIndex: number) => {
    if (getValues("ShippingType") !== ShippingTypeBookingTemplates.LL) {
      setValue(`Details.${cargoIndex}.Refrigerated`, true);
    }
  };

  useEffect(() => {
    WindowUtilities.scrollToTop();
    dispatch(getContainerTypes());
    dispatch(getPackageTypes());
    dispatch(getAllCommodities());
  }, []);

  return (
    <Form id="booking_templates_cargo_form">
      <ShipmentSection
        onShipmentTypeChange={handleResetDetails}
        register={register}
      />
      <Divider />
      <CargoSection
        control={control}
        onCommodityClear={handleResetDescription}
        onShipmentPackagingTypeClear={resetTemperatureFields}
        onTemperatureClear={resetVentilation}
        onTemperatureContainerChosen={handleSetTemperatureControl}
        onTemperatureControlChange={resetTemperatureControlledFields}
        onTemperatureOutOfBounds={resetTemperatureControlledFields}
        register={register}
      />
      <Divider />
      <HSCodesSection control={control} />
      <Divider />
      <BillOfLadingSection control={control} />
      <Divider />
      <CommentsSection register={register} />
      <Divider />
      <div className="flex flex-row justify-between">
        <LeavePageModal
          //Navigate to templates list page
          isLink
          onLeave={() => {
            onCancelClick();
            navigate(
              `${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}`
            );
          }}
        />
        <div className="flex flex-row justify-end gap-3">
          <Button onClick={handleSubmit(handlePrevClick)} variant="secondary">
            {t("LABEL_BACK")}
          </Button>

          <Button
            loading={isSubmitting}
            onClick={handleSubmit(handleNextClick)}
            variant="primary"
          >
            {t("LABEL_SAVE_TEMPLATE")}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default BookingTemplatesCargo;
